import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Alert, Button, Dropdown, Form, Modal, Spinner } from "react-bootstrap";
import { API_BASE_URL } from "../../utils/constants";
import { readErrorMessage } from "../../utils/apiError";
import MatchBlanks from "./MatchBlanks";
import AcceptChanges from "./AcceptChanges";

// On-screen editor of an uploaded Word format. Left: the document as simple paragraphs with tools (fill-ins,
// "print only when…", "repeat for every…", edit text, delete). Every change is made in the Word file itself.
// Right: the real PDF of a real case, made the same way as the final document, refreshed after each change.

const api = (id, path = "") => `${API_BASE_URL}/api/doc-templates/uploads/${id}${path}`;

const COLORS = {
  fill: { background: "#e7f1ff", border: "1px solid #9ec5fe", color: "#084298" },
  blank: { background: "#fff3cd", border: "1px solid #ffda6a", color: "#664d03" },
  mark: { background: "#f3e8ff", border: "1px solid #c29ffa", color: "#59359a" },
};
const chip = (style) => ({ ...style, borderRadius: "4px", padding: "0 4px", whiteSpace: "nowrap", fontSize: "0.92em" });

// ---------- document view ----------

const Segment = ({ seg, onBlank, onPicture }) => {
  switch (seg.kind) {
    case "text": {
      const style = { fontWeight: seg.b ? 700 : undefined, fontStyle: seg.i ? "italic" : undefined, textDecoration: seg.u ? "underline" : undefined };
      return (
        <span data-start={seg.start} style={style}>
          {seg.text}
        </span>
      );
    }
    case "fill":
      return (
        <span data-start={seg.start} data-end={seg.end} contentEditable={false} title={`Prints: ${seg.label}`} style={chip(COLORS.fill)}>
          {seg.label}
        </span>
      );
    case "blank":
      return (
        <span
          data-start={seg.start}
          data-end={seg.end}
          contentEditable={false}
          role="button"
          title="A blank from the bank's file. Click to choose what it prints."
          onClick={(e) => {
            e.stopPropagation();
            onBlank();
          }}
          style={chip(COLORS.blank)}
        >
          «{seg.name}»
        </span>
      );
    case "mark":
      return (
        <span data-start={seg.start} data-end={seg.end} contentEditable={false} style={chip(COLORS.mark)}>
          {seg.label}
        </span>
      );
    case "tab":
      return <span style={{ display: "inline-block", width: "2em" }} />;
    case "break":
      return <br />;
    case "picture":
      return (
        <span
          contentEditable={false}
          role="button"
          title="Click to choose what this picture shows"
          onClick={(e) => {
            e.stopPropagation();
            if (seg.index >= 0) onPicture(seg);
          }}
          style={chip(
            seg.fillIn
              ? { background: "#d1e7dd", border: "1px solid #75b798", color: "#0f5132" }
              : { background: "#eee", border: "1px solid #ccc", color: "#555" },
          )}
        >
          🖼 {seg.fillIn ? `${seg.label} – of each case's arbitrator` : "Picture – same on every document"}
        </span>
      );
    default:
      return null;
  }
};

const textLength = (p) => (p.segments || []).reduce((n, s) => Math.max(n, s.end ?? (s.start !== undefined ? s.start + (s.text?.length || 0) : 0)), 0);

const ParagraphView = ({ block, selected, depth, onSelect, onBlank, onPicture }) => {
  if (block.type === "marker") {
    return (
      <div
        data-paragraph={block.id}
        onClick={(e) => onSelect(block.id, e.shiftKey)}
        style={{
          ...COLORS.mark,
          margin: `4px 0 4px ${depth * 14}px`,
          padding: "2px 8px",
          borderRadius: "4px",
          fontSize: "11px",
          cursor: "pointer",
          outline: selected ? "2px solid #0d6efd" : undefined,
        }}
      >
        {block.open ? "▼ " : "▲ "}
        {block.label}
      </div>
    );
  }
  const empty = !block.segments.length;
  return (
    <div
      data-paragraph={block.id}
      data-length={textLength(block)}
      onClick={(e) => onSelect(block.id, e.shiftKey)}
      style={{
        textAlign: { both: "justify", center: "center", right: "right", end: "right" }[block.align] || "left",
        minHeight: "1.4em",
        padding: "1px 6px",
        marginLeft: `${depth * 14}px`,
        borderLeft: depth ? "3px solid #c29ffa" : "3px solid transparent",
        background: selected ? "#e7f1ff" : undefined,
        cursor: "text",
        whiteSpace: "pre-wrap",
      }}
    >
      {empty ? <span className="text-muted" style={{ fontSize: "10px" }}>¶</span> : block.segments.map((seg, i) => <Segment key={i} seg={seg} onBlank={onBlank} onPicture={onPicture} />)}
    </div>
  );
};

// Paragraph offset of a point of the browser's text selection
function offsetAt(node, offset) {
  let el = node.nodeType === 3 ? node.parentElement : node;
  if (el?.dataset?.paragraph !== undefined && node.nodeType !== 3) {
    const kids = Array.from(el.children);
    const next = kids[offset];
    if (next?.dataset?.start !== undefined) return Number(next.dataset.start);
    return Number(el.dataset.length || 0);
  }
  while (el && el.dataset?.start === undefined && el.dataset?.paragraph === undefined) el = el.parentElement;
  if (!el || el.dataset.start === undefined) return null;
  if (el.dataset.end !== undefined) return offset > 0 ? Number(el.dataset.end) : Number(el.dataset.start); // whole chip
  return Number(el.dataset.start) + (node.nodeType === 3 ? offset : 0);
}

function readTextSelection() {
  const sel = window.getSelection();
  if (!sel || !sel.rangeCount) return null;
  const range = sel.getRangeAt(0);
  const paragraphOf = (n) => (n.nodeType === 3 ? n.parentElement : n)?.closest?.("[data-paragraph]");
  const pa = paragraphOf(range.startContainer);
  const pb = paragraphOf(range.endContainer);
  if (!pa || pa !== pb || pa.dataset.length === undefined) return null;
  const start = offsetAt(range.startContainer, range.startOffset);
  const end = offsetAt(range.endContainer, range.endOffset);
  if (start === null || end === null) return null;
  return { paragraph: Number(pa.dataset.paragraph), start: Math.min(start, end), end: Math.max(start, end), text: sel.toString() };
}

// ---------- editor ----------

const TemplateEditor = ({ show, uploadId, doc, variation, onClose }) => {
  const [view, setView] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [selected, setSelected] = useState(null); // { from, to } paragraph ids
  const [textSel, setTextSel] = useState(null); // { paragraph, start, end, text }
  const [editText, setEditText] = useState(null); // { paragraph, start, end, value }
  const [layoutBox, setLayoutBox] = useState(null); // "page" | "paragraph", with the values being edited
  const [layoutValues, setLayoutValues] = useState({});
  const [showBlanks, setShowBlanks] = useState(false);
  const [pictureChoice, setPictureChoice] = useState(null); // picture segment being set

  const [cases, setCases] = useState({ cases: [] });
  const [caseId, setCaseId] = useState("");
  const [search, setSearch] = useState("");
  const [previewVersion, setPreviewVersion] = useState(0);
  const [preview, setPreview] = useState({ url: null, loading: false, error: "" });
  const docRef = useRef(null);

  const load = useCallback(async () => {
    setError("");
    const res = await fetch(api(uploadId, "/editor"));
    if (!res.ok) throw new Error(await readErrorMessage(res));
    const result = await res.json();
    setView(result);
    setShowBlanks(result.blanks?.length > 0); // blanks come first: open "Match the blanks" straight away
  }, [uploadId]);

  useEffect(() => {
    if (!show || !uploadId) return;
    setView(null);
    setSelected(null);
    setTextSel(null);
    load().catch((e) => setError(e.message));
  }, [show, uploadId, load]);

  // Real cases to preview with; the first example case is shown at once
  useEffect(() => {
    if (!show || !doc) return;
    const timer = setTimeout(() => {
      fetch(
        `${API_BASE_URL}/api/doc-templates/preview-cases?docType=${doc.key}&variation=${variation?.key || "main"}&q=${encodeURIComponent(search)}`,
      )
        .then(async (res) => {
          if (!res.ok) throw new Error(await readErrorMessage(res));
          const result = await res.json();
          setCases(result);
          setCaseId((current) => current || String(result.suggested || ""));
        })
        .catch((e) => setError(e.message));
    }, 300);
    return () => clearTimeout(timer);
  }, [show, doc, variation, search]);

  const run = async (request) => {
    setBusy(true);
    setError("");
    try {
      const res = await request();
      if (!res.ok) throw new Error(await readErrorMessage(res));
      setView(await res.json());
      setTextSel(null);
      setPreviewVersion((v) => v + 1);
      return true;
    } catch (e) {
      setError(e.message);
      return false;
    } finally {
      setBusy(false);
    }
  };
  const post = (path, body) =>
    run(() => fetch(api(uploadId, path), { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body || {}) }));
  const edit = (body) => post("/edit", body);

  // Top-level blocks with how deep inside marked parts each one is
  const rendered = useMemo(() => {
    let depth = 0;
    return (view?.blocks || []).map((block) => {
      if (block.type === "marker" && !block.open) depth = Math.max(0, depth - 1);
      const item = { block, depth };
      if (block.type === "marker" && block.open) depth += 1;
      return item;
    });
  }, [view]);

  const allParagraphs = useMemo(() => {
    const out = [];
    for (const b of view?.blocks || []) {
      if (b.type === "table") b.rows.forEach((r) => r.cells.flat().forEach((p) => out.push({ ...p, rowRepeat: r.repeat, inTable: true })));
      else out.push(b);
    }
    return out;
  }, [view]);
  const paragraphById = (id) => allParagraphs.find((p) => p.id === id);

  const select = (id, extend) => {
    setSelected((cur) => (extend && cur ? { from: Math.min(cur.from, id), to: Math.max(cur.to, id) } : { from: id, to: id }));
  };
  const isSelected = (id) => selected && id >= selected.from && id <= selected.to;
  const first = selected && paragraphById(selected.from);

  const onMouseUp = () => {
    const sel = readTextSelection();
    setTextSel(sel);
    if (sel) setSelected({ from: sel.paragraph, to: sel.paragraph });
  };

  // ----- tools -----
  const fillIns = (doc?.variables || []).filter((v) => v.kind === "text");
  const listFields = (doc?.variables || [])
    .filter((v) => v.kind === "list")
    .flatMap((l) => l.fields.filter((f) => f.kind === "text").map((f) => ({ ...f, group: `For every: ${l.label}` })));
  // Lists, and lists inside a list's items (e.g. each person's other addresses: use inside that person's repeat)
  const lists = (doc?.variables || [])
    .filter((v) => v.kind === "list")
    .flatMap((l) => [l, ...(l.fields || []).filter((f) => f.kind === "list").map((f) => ({ ...f, label: `${f.label} (inside "${l.label}")` }))]);

  const putFillIn = (key) => {
    if (!textSel) return;
    edit({ op: "replaceText", paragraph: textSel.paragraph, start: textSel.start, end: textSel.end, text: `{{${key}}}` });
  };

  const fillInMenu = (label, disabled) => (
    <Dropdown>
      <Dropdown.Toggle size="sm" variant="outline-primary" disabled={disabled || busy}>
        {label}
      </Dropdown.Toggle>
      <Dropdown.Menu style={{ maxHeight: "60vh", overflowY: "auto", fontSize: "12px" }}>
        {[...new Set([...fillIns, ...listFields].map((v) => v.group))].map((group) => (
          <React.Fragment key={group}>
            <Dropdown.Header>{group}</Dropdown.Header>
            {[...fillIns, ...listFields]
              .filter((v) => v.group === group)
              .map((v) => (
                <Dropdown.Item key={`${group}.${v.key}`} onClick={() => putFillIn(v.key)}>
                  {v.label} <span className="text-muted">e.g. {v.example}</span>
                </Dropdown.Item>
              ))}
          </React.Fragment>
        ))}
      </Dropdown.Menu>
    </Dropdown>
  );

  const repeatMenu = (
    <Dropdown>
      <Dropdown.Toggle size="sm" variant="outline-primary" disabled={!selected || busy || first?.type === "marker"}>
        {first?.inTable ? "Repeat this table row for every…" : "Repeat for every…"}
      </Dropdown.Toggle>
      <Dropdown.Menu style={{ fontSize: "12px" }}>
        {lists.map((l) => (
          <Dropdown.Item
            key={l.key}
            onClick={() =>
              first?.inTable
                ? edit({ op: "markRow", paragraph: selected.from, name: l.key })
                : edit({ op: "mark", from: selected.from, to: selected.to, kind: "repeat", name: l.key })
            }
          >
            {l.label}
          </Dropdown.Item>
        ))}
      </Dropdown.Menu>
    </Dropdown>
  );

  const canUnmark = first && (first.type === "marker" || first.rowRepeat);
  const problems = view?.problems || [];

  // ----- preview: the real PDF, made again after every change -----
  useEffect(() => {
    if (!show || !uploadId || !caseId) return;
    let cancelled = false;
    let url = null;
    setPreview((p) => ({ ...p, loading: true, error: "" }));
    fetch(`${api(uploadId, "/preview")}?caseId=${caseId}`)
      .then(async (res) => {
        if (!res.ok) throw new Error(await readErrorMessage(res));
        url = URL.createObjectURL(await res.blob());
        if (cancelled) return;
        setPreview((p) => {
          if (p.url) URL.revokeObjectURL(p.url);
          return { url, loading: false, error: "" };
        });
      })
      .catch((e) => !cancelled && setPreview((p) => ({ ...p, loading: false, error: e.message })));
    return () => {
      cancelled = true;
    };
  }, [show, uploadId, caseId, previewVersion]);
  const caseLabel = (c) =>
    `${c.referenceNo} · ${c.borrowerName}${c.coBorrowers ? ` +${c.coBorrowers}` : ""} · ${c.bank} · ${c.arbitrator || "no arbitrator yet"}${c.fitsVariation ? "" : " (other kind)"}${c.reachedStage ? "" : " (not at this stage)"}`;

  return (
    <Modal show={show} onHide={() => onClose(view)} fullscreen backdrop="static">
      <Modal.Header closeButton closeVariant="white" className="customModal">
        <Modal.Title style={{ fontSize: "15px" }}>
          Edit: {doc?.label}
          {variation && doc?.variations?.length > 1 && ` – ${variation.label}`}
          {view && <span className="ms-2 fw-normal">({view.originalName})</span>}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body className="p-0" style={{ fontSize: "12px", overflow: "hidden" }}>
        <div className="d-flex h-100">
          {/* ── Left: document + tools ── */}
          <div className="d-flex flex-column" style={{ width: "52%", borderRight: "1px solid #dee2e6" }}>
            <div className="p-2 border-bottom bg-light d-flex flex-wrap gap-1 align-items-center">
              {fillInMenu(textSel && textSel.start !== textSel.end ? "Make fill-in" : "Insert fill-in", !textSel)}
              <Button
                size="sm"
                variant="outline-primary"
                disabled={!textSel || busy}
                onClick={() => setEditText({ ...textSel, value: textSel.text })}
              >
                {textSel && textSel.start !== textSel.end ? "Change text" : "Type text"}
              </Button>
              {repeatMenu}
              <Dropdown>
                <Dropdown.Toggle size="sm" variant="outline-primary" disabled={!textSel || busy} title="Click in the text where it should go first">
                  Insert signature
                </Dropdown.Toggle>
                <Dropdown.Menu style={{ fontSize: "12px" }}>
                  {[
                    ["ARB_SIGNATURE", "Arbitrator's signature"],
                    ["ARB_STAMP", "Arbitrator's stamp"],
                  ].map(([key, label]) => (
                    <Dropdown.Item
                      key={key}
                      onClick={() => edit({ op: "insertPicture", paragraph: textSel.paragraph, at: textSel.end, fillIn: key })}
                    >
                      {label}
                    </Dropdown.Item>
                  ))}
                </Dropdown.Menu>
              </Dropdown>
              {canUnmark && (
                <Button
                  size="sm"
                  variant="outline-secondary"
                  disabled={busy}
                  onClick={() => edit({ op: "unmark", paragraph: selected.from })}
                >
                  Remove this mark
                </Button>
              )}
              <Dropdown>
                <Dropdown.Toggle size="sm" variant="outline-primary" disabled={busy}>
                  Layout
                </Dropdown.Toggle>
                <Dropdown.Menu style={{ fontSize: "12px" }}>
                  <Dropdown.Item
                    disabled={!selected || first?.type !== "paragraph"}
                    onClick={() => {
                      setLayoutValues({ align: first.align || "left", ...first.layout });
                      setLayoutBox("paragraph");
                    }}
                  >
                    Spacing &amp; alignment of the chosen paragraph(s)…
                  </Dropdown.Item>
                  <Dropdown.Item
                    disabled={!view?.page}
                    onClick={() => {
                      setLayoutValues({ ...view.page });
                      setLayoutBox("page");
                    }}
                  >
                    Page margins…
                  </Dropdown.Item>
                  <Dropdown.Divider />
                  <Dropdown.Item
                    disabled={!textSel}
                    onClick={() => edit({ op: "insertBreak", paragraph: textSel.paragraph, at: textSel.end, kind: "line" })}
                  >
                    Start a new line here
                  </Dropdown.Item>
                  <Dropdown.Item
                    disabled={!textSel}
                    onClick={() => edit({ op: "insertBreak", paragraph: textSel.paragraph, at: textSel.end, kind: "page" })}
                  >
                    Start a new page here
                  </Dropdown.Item>
                </Dropdown.Menu>
              </Dropdown>
              <Button
                size="sm"
                variant="outline-danger"
                disabled={!selected || busy}
                onClick={() =>
                  window.confirm("Delete the selected paragraph(s)?") && edit({ op: "delete", from: selected.from, to: selected.to })
                }
              >
                Delete
              </Button>
              <Button size="sm" variant="outline-secondary" disabled={!view?.undoSteps || busy} onClick={() => post("/undo")}>
                Undo
              </Button>
              {busy && <Spinner animation="border" size="sm" className="ms-1" />}
            </div>
            <div className="px-2 py-1 border-bottom text-muted" style={{ fontSize: "11px" }}>
              Click a paragraph to pick it (Shift + click to pick several). Select words to turn them into a fill-in or
              change them. <span style={chip(COLORS.fill)}>blue</span> = fill-in,{" "}
              <span style={chip(COLORS.blank)}>yellow</span> = bank's blank, <span style={chip(COLORS.mark)}>purple</span> = part
              repeated for every person / account.
            </div>

            {error && (
              <Alert variant="danger" className="m-2 py-1" onClose={() => setError("")} dismissible>
                {error}
              </Alert>
            )}

            {view && (problems.length > 0 || view.blanks?.length > 0) && (
              <div className="m-2 p-2 rounded" style={{ background: "#fdecea", fontSize: "11px" }}>
                <div className="d-flex justify-content-between">
                  <strong className="text-danger">
                    {problems.length} thing{problems.length === 1 ? "" : "s"} to fix before this file can be used
                  </strong>
                  {view.blanks?.length > 0 && (
                    <button type="button" className="btn btn-link btn-sm p-0" onClick={() => setShowBlanks((s) => !s)}>
                      {showBlanks ? "Hide" : "Match the blanks"}
                    </button>
                  )}
                </div>
                <AcceptChanges
                  upload={view}
                  onAccepted={(result) => {
                    setView(result);
                    setPreviewVersion((v) => v + 1);
                  }}
                />
                {showBlanks && view.blanks?.length > 0 && (
                  <MatchBlanks
                    upload={view}
                    doc={doc}
                    onMatched={(result) => {
                      setView(result);
                      setPreviewVersion((v) => v + 1);
                    }}
                  />
                )}
                <ul className="mb-0 ps-3">
                  {problems.map((p, i) => (
                    <li key={i}>
                      {p.problem} <span className="text-muted">— {p.fix}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {view && !problems.length && (
              <div className="m-2 py-1 px-2 rounded" style={{ background: "#d1e7dd", fontSize: "11px" }}>
                No problems — this file is ready.
              </div>
            )}

            <div ref={docRef} onMouseUp={onMouseUp} className="flex-grow-1 overflow-auto p-3" style={{ background: "#fff", fontFamily: "Georgia, serif", fontSize: "13px" }}>
              {!view && !error && <Spinner animation="border" size="sm" />}
              {rendered.map(({ block, depth }, i) =>
                block.type === "table" ? (
                  <table key={`t${i}`} className="table table-bordered table-sm my-2" style={{ marginLeft: `${depth * 14}px`, fontSize: "12px" }}>
                    <tbody>
                      {block.rows.map((row, r) => (
                        <tr key={r} style={row.repeat ? { outline: "2px solid #c29ffa" } : undefined} title={row.repeat ? "This row repeats" : undefined}>
                          {row.cells.map((cell, c) => (
                            <td key={c} className="p-0">
                              {cell.map((p) => (
                                <ParagraphView key={p.id} block={p} depth={0} selected={isSelected(p.id)} onSelect={select} onBlank={() => setShowBlanks(true)} onPicture={setPictureChoice} />
                              ))}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <ParagraphView key={block.id} block={block} depth={depth} selected={isSelected(block.id)} onSelect={select} onBlank={() => setShowBlanks(true)} onPicture={setPictureChoice} />
                ),
              )}
            </div>
          </div>

          {/* ── Right: real PDF of a real case ── */}
          <div className="d-flex flex-column" style={{ width: "48%" }}>
            <div className="p-2 border-bottom bg-light">
              <div className="mb-1">
                <strong>Preview with a real case</strong>
                {variation && doc?.variations?.length > 1 && <span className="ms-1">of this kind: {variation.label}</span>}
              </div>
              {cases.noFittingCase && (
                <div className="mb-1 px-2 py-1 rounded" style={{ background: "#fff3cd", fontSize: "11px" }}>
                  {cases.noFittingCase}
                </div>
              )}
              <div className="d-flex gap-1">
                <Form.Control
                  size="sm"
                  placeholder="Search reference no. or borrower…"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  style={{ maxWidth: "220px", fontSize: "12px" }}
                />
                <Form.Select size="sm" value={caseId} onChange={(e) => setCaseId(e.target.value)} style={{ fontSize: "12px" }}>
                  {!cases.cases.some((c) => String(c.caseId) === caseId) && caseId && <option value={caseId}>Selected case</option>}
                  {cases.cases.map((c) => (
                    <option key={c.caseId} value={c.caseId}>
                      {caseLabel(c)}
                    </option>
                  ))}
                </Form.Select>
              </div>
              <div className="text-muted mt-1" style={{ fontSize: "11px" }}>
                Real case from the system, printed exactly as the final document: its own bank, borrowers and arbitrator.
                Pick another case to see its details. {cases.stageNote}
              </div>
            </div>
            <div className="flex-grow-1 position-relative" style={{ background: "#525659" }}>
              {preview.loading && (
                <div className="position-absolute top-0 start-0 m-2 px-2 py-1 rounded bg-white" style={{ zIndex: 2 }}>
                  <Spinner animation="border" size="sm" /> Making the PDF…
                </div>
              )}
              {preview.error && (
                <Alert variant="danger" className="position-absolute top-0 start-0 end-0 m-2" style={{ zIndex: 2 }}>
                  The preview could not be made: {preview.error}
                </Alert>
              )}
              {preview.url ? (
                <iframe title="Preview" src={preview.url} style={{ width: "100%", height: "100%", border: 0 }} />
              ) : (
                !preview.loading && <div className="text-white p-3">{caseId ? "" : "No case to preview yet."}</div>
              )}
            </div>
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <span className="me-auto text-muted">Changes are saved in the Word file as you make them. Save the bank / product to start using it.</span>
        <Button size="sm" variant="primary" onClick={() => onClose(view)}>
          Done
        </Button>
      </Modal.Footer>

      {/* Page margins / paragraph spacing and alignment */}
      <Modal show={Boolean(layoutBox)} onHide={() => setLayoutBox(null)} centered>
        <Modal.Header closeButton>
          <Modal.Title style={{ fontSize: "14px" }}>
            {layoutBox === "page" ? "Page margins" : "Spacing & alignment"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ fontSize: "12px" }}>
          {layoutBox === "page" ? (
            <>
              <div className="text-muted mb-2">Empty space around the text on every page, in cm.</div>
              <div className="row g-2">
                {[
                  ["top", "Top"],
                  ["bottom", "Bottom"],
                  ["left", "Left"],
                  ["right", "Right"],
                ].map(([key, label]) => (
                  <div className="col-6" key={key}>
                    <Form.Label className="mb-0">{label} (cm)</Form.Label>
                    <Form.Control
                      type="number"
                      step="0.1"
                      min="0"
                      size="sm"
                      value={layoutValues[key] ?? ""}
                      onChange={(e) => setLayoutValues((v) => ({ ...v, [key]: e.target.value }))}
                    />
                  </div>
                ))}
              </div>
            </>
          ) : (
            <>
              <div className="text-muted mb-2">
                For the paragraph(s) you picked
                {selected && selected.to !== selected.from ? ` (${selected.to - selected.from + 1} paragraphs)` : ""}. Leave a box
                empty to keep it as it is.
              </div>
              <Form.Label className="mb-0">Line up the text</Form.Label>
              <div className="d-flex gap-1 mb-2">
                {[
                  ["left", "Left"],
                  ["center", "Centre"],
                  ["right", "Right"],
                  ["both", "Both edges (justified)"],
                ].map(([key, label]) => (
                  <Button
                    key={key}
                    size="sm"
                    variant={layoutValues.align === key ? "primary" : "outline-secondary"}
                    onClick={() => setLayoutValues((v) => ({ ...v, align: key }))}
                  >
                    {label}
                  </Button>
                ))}
              </div>
              <div className="row g-2">
                {[
                  ["spaceBefore", "Space above (pt)", "1"],
                  ["spaceAfter", "Space below (pt)", "1"],
                  ["indentLeft", "Push in from left (cm)", "0.1"],
                  ["indentRight", "Push in from right (cm)", "0.1"],
                ].map(([key, label, step]) => (
                  <div className="col-6" key={key}>
                    <Form.Label className="mb-0">{label}</Form.Label>
                    <Form.Control
                      type="number"
                      step={step}
                      size="sm"
                      value={layoutValues[key] ?? ""}
                      onChange={(e) => setLayoutValues((v) => ({ ...v, [key]: e.target.value }))}
                    />
                  </div>
                ))}
                <div className="col-6">
                  <Form.Label className="mb-0">Gap between lines</Form.Label>
                  <Form.Select
                    size="sm"
                    value={layoutValues.lineSpacing ?? ""}
                    onChange={(e) => setLayoutValues((v) => ({ ...v, lineSpacing: e.target.value }))}
                  >
                    <option value="">Keep as it is</option>
                    <option value="1">Single</option>
                    <option value="1.15">1.15</option>
                    <option value="1.5">1.5</option>
                    <option value="2">Double</option>
                  </Form.Select>
                </div>
                <div className="col-6 d-flex align-items-end">
                  <Form.Check
                    type="checkbox"
                    id="keep-with-next"
                    label="Keep on the same page as the next paragraph"
                    checked={Boolean(layoutValues.keepWithNext)}
                    onChange={(e) => setLayoutValues((v) => ({ ...v, keepWithNext: e.target.checked }))}
                  />
                </div>
              </div>
            </>
          )}
          <div className="text-muted mt-2">The preview on the right shows the result after you apply.</div>
        </Modal.Body>
        <Modal.Footer>
          <Button size="sm" variant="secondary" onClick={() => setLayoutBox(null)}>
            Cancel
          </Button>
          <Button
            size="sm"
            disabled={busy}
            onClick={async () => {
              const num = (value) => (value === "" || value === undefined || value === null ? undefined : Number(value));
              const body =
                layoutBox === "page"
                  ? {
                      op: "setMargins",
                      top: num(layoutValues.top) ?? 0,
                      right: num(layoutValues.right) ?? 0,
                      bottom: num(layoutValues.bottom) ?? 0,
                      left: num(layoutValues.left) ?? 0,
                    }
                  : {
                      op: "setParagraphLayout",
                      from: selected.from,
                      to: selected.to,
                      align: layoutValues.align,
                      spaceBefore: num(layoutValues.spaceBefore),
                      spaceAfter: num(layoutValues.spaceAfter),
                      lineSpacing: num(layoutValues.lineSpacing),
                      indentLeft: num(layoutValues.indentLeft),
                      indentRight: num(layoutValues.indentRight),
                      keepWithNext: Boolean(layoutValues.keepWithNext),
                    };
              if (await edit(body)) setLayoutBox(null);
            }}
          >
            Apply
          </Button>
        </Modal.Footer>
      </Modal>

      {/* What a picture of the file shows */}
      <Modal show={Boolean(pictureChoice)} onHide={() => setPictureChoice(null)} centered>
        <Modal.Header closeButton>
          <Modal.Title style={{ fontSize: "14px" }}>What should this picture show?</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ fontSize: "12px" }}>
          {[
            ["ARB_SIGNATURE", "The signature of the case's arbitrator", "Each document shows the signature uploaded for its own arbitrator (Arbitrators → edit)."],
            ["ARB_STAMP", "The stamp of the case's arbitrator", "Each document shows the stamp uploaded for its own arbitrator."],
            ["", "This same picture on every document", "For a logo or the institution's stamp: printed exactly as in the Word file."],
          ].map(([key, title, hint]) => (
            <button
              key={key || "fixed"}
              type="button"
              className={`btn w-100 text-start mb-2 ${(pictureChoice?.fillIn || "") === key ? "btn-primary" : "btn-outline-secondary"}`}
              style={{ fontSize: "12px" }}
              disabled={busy}
              onClick={async () => {
                const ok = await edit({ op: "setPicture", index: pictureChoice.index, fillIn: key });
                if (ok) setPictureChoice(null);
              }}
            >
              <div className="fw-semibold">{title}</div>
              <div className={(pictureChoice?.fillIn || "") === key ? "" : "text-muted"}>{hint}</div>
            </button>
          ))}
          <div className="text-muted">The picture keeps its place and size; a signature is fitted inside that box.</div>
        </Modal.Body>
      </Modal>

      {/* Change / type words */}
      <Modal show={Boolean(editText)} onHide={() => setEditText(null)} centered>
        <Modal.Header closeButton>
          <Modal.Title style={{ fontSize: "14px" }}>{editText && editText.start !== editText.end ? "Change text" : "Type text"}</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ fontSize: "12px" }}>
          {editText && editText.start !== editText.end && (
            <div className="mb-2 text-muted">
              Now: “{editText.text}”
            </div>
          )}
          <Form.Control
            as="textarea"
            rows={3}
            autoFocus
            value={editText?.value || ""}
            onChange={(e) => setEditText((t) => ({ ...t, value: e.target.value }))}
            style={{ fontSize: "12px" }}
          />
          <div className="text-muted mt-1">The new words take the formatting of the text they replace.</div>
        </Modal.Body>
        <Modal.Footer>
          <Button size="sm" variant="secondary" onClick={() => setEditText(null)}>
            Cancel
          </Button>
          <Button
            size="sm"
            onClick={async () => {
              const ok = await edit({ op: "replaceText", paragraph: editText.paragraph, start: editText.start, end: editText.end, text: editText.value });
              if (ok) setEditText(null);
            }}
          >
            Apply
          </Button>
        </Modal.Footer>
      </Modal>
    </Modal>
  );
};

export default TemplateEditor;
